import {FC, PropsWithChildren} from 'react';
import {ErrorBoundary} from '@sentry/react';
import clsx from 'clsx';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router';
import {ToastContainer} from 'react-toastify';

import {APP_PATH, FORM_PATH} from '@/constants/routes';
import {useNetworkStatus} from '@/contexts/network-status-context';
import environment from '@/environment';
import {useInspector} from '../document/hooks/use-inspector';
import {ErrorFallback} from '../error-fallback';
import OneTrustInitializer from '../initializers/one-trust';
import TagManagerInitializer from '../initializers/tag-manager';
import OfflineContainer from '../offline-container';

const AppShell: FC<PropsWithChildren> = ({children}) => {
  const {i18n} = useTranslation();
  const {isOffline} = useNetworkStatus();
  const fullWidth = useShouldDisplayFullWidth();

  const oneTrustSiteId = environment.ONE_TRUST_SITE_ID;
  const googleTagManagerContainerId = environment.GOOGLE_TAG_MANAGER_CONTAINER_ID;

  return (
    <>
      <Helmet>
        <meta name="application-version" content={APP_VERSION} />
      </Helmet>

      {googleTagManagerContainerId && <TagManagerInitializer id={googleTagManagerContainerId} />}
      {oneTrustSiteId && <OneTrustInitializer id={oneTrustSiteId} locale={i18n.language} />}

      <div
        data-testid="app"
        className={clsx('mx-auto flex h-full flex-col bg-greige px-4 pb-6', fullWidth ? 'mx-10' : 'max-w-7xl')}
      >
        <ToastContainer />

        <ErrorBoundary fallback={<ErrorFallback />}>{children}</ErrorBoundary>

        {isOffline && (
          <div className="absolute inset-x-0 top-32 flex justify-center">
            <OfflineContainer />
          </div>
        )}
      </div>
    </>
  );
};

export default AppShell;

const useShouldDisplayFullWidth = (): boolean => {
  const location = useLocation();

  const isInForm = location.pathname.startsWith(FORM_PATH);
  const isOnHomePage = location.pathname === APP_PATH;
  const isInspectorPanelOpen = useInspector(state => state.isVisible);

  return (isInForm || isOnHomePage) && isInspectorPanelOpen;
};
